import { useMemo } from 'react';
import type {
  ExecutiveRelationDataSource,
  VendorQccSeniorPersonInfoDto,
  VendorQccPersonHisJobCompanyInfoDto,
  VendorQccPersonHoldingCompanyInfoDto,
  ItemDTO,
} from '@purchase/supplier';

/**
 * 从按 personName 聚合的 person 数组中,提取指定人名的 items 列表。
 * 后端三组关联画像(VendorQccSeniorPersonInfoDto / PersonHisJobCompanyInfoDto /
 * PersonHoldingCompanyInfoDto)顶层均按 personName 索引,每项含 items: ItemDto[]。
 *
 * 入参 personName 与 group.personName 严格相等匹配,
 * 双方都通过 normalizeName 归一化(空串/undefined/null 都映射到 null)。
 */
function pickItemsByPersonName(
  groups:
    | VendorQccSeniorPersonInfoDto[]
    | VendorQccPersonHisJobCompanyInfoDto[]
    | VendorQccPersonHoldingCompanyInfoDto[]
    | undefined,
  personName: string
): NonNullable<ItemDTO[]> {
  if (!groups) return [] as any;
  const matched = groups.filter((g) => g.personName === personName);
  const merged = matched.flatMap((g) => g.items ?? []);
  return merged;
}

/**
 * 获取指定人员的对外关联关系数据(投资任职 + 控制企业)。
 *
 * - 当前对外任职 ← vendorQccSeniorPersonInfos
 * - 历史对外任职 ← vendorQccPersonHisJobCompanyInfos
 * - 控制企业    ← vendorQccPersonHoldingCompanyInfos
 *
 * 关联 key 是 personName,不是 personId — 后端没有 person 维度的全域稳定 ID。
 */
export function useExecutiveRelationData(
  personName: string | undefined,
  formDetail: any
): ExecutiveRelationDataSource {
  return useMemo(() => {
    if (!personName)
      return {
        externalPositions: { current: [], history: [] },
        controlledEnterprises: [],
      };

    const seniorPersonInfos: VendorQccSeniorPersonInfoDto[] | undefined =
      formDetail?.vendorQccSeniorPersonInfos;
    const hisJobInfos: VendorQccPersonHisJobCompanyInfoDto[] | undefined =
      formDetail?.vendorQccPersonHisJobCompanyInfos;
    const holdingInfos: VendorQccPersonHoldingCompanyInfoDto[] | undefined =
      formDetail?.vendorQccPersonHoldingCompanyInfos;

    return {
      externalPositions: {
        current: pickItemsByPersonName(seniorPersonInfos, personName),
        history: pickItemsByPersonName(hisJobInfos, personName),
      },
      controlledEnterprises: pickItemsByPersonName(holdingInfos, personName),
    };
  }, [personName, formDetail]);
}
